import JSZip from 'jszip';
import {
    useEffect,
    useState
} from 'react';

import { getProfileInfo } from '../screens/Services/Backup/common/backupData';

const ENCODING = 'utf8';

const pack = (buf) =>
    new JSZip().file('content.txt', buf)
        .generateAsync({
            type: 'base64',
            compression: 'DEFLATE',
            compressionOptions: { level: 1 },
        });
const unpack = (buf) => JSZip.loadAsync(buf, { base64: true })
    .then((zip) => zip.file('content.txt')
        .async('string'));

/**
 * Local storage.
 *
 * This implementation based on `Object.fetchBlob.fs`.
 */
export class LocalStorage {

    constructor(useDebug) {
        this.cache = {};
        this.debug = useDebug;
    }

    get profileId() {
        const { profileId: pId } = getProfileInfo();
        return Object.currentProfileId ?? pId ?? 'default';
    }

    get hashCode() {
        const { hashCode: hC } = getProfileInfo();
        return Object.currentHashCode ?? hC;
    }

    // eslint-disable-next-line class-methods-use-this
    get fs() {
        return Object.fetchBlob.fs;
    }

    log (...args) {
        if (!this.debug) {
            return;
        }
        console.log(`%c[STORAGE] ${args?.[0]}`, 'background: #222, color: #bada55', [...args ?? []].slice(1));
    }

    profileKey(key) {
        const result = `${this.profileId}_${key}`;
        this.log(`profileKey for ${key}`, result);
        return result;
    }

    pathForKey(key) {
        const result = `${`${this.fs.dirs.DocumentDir}/storage`}/${key}`;
        this.log(`pathForKey for ${key}`, result);
        return result;
    }

    ls(key = '') {
        const result = this.fs.ls(this.pathForKey(key));
        this.log('LS', result);
        return result;
    }

    stat(key) {
        return this.fs
            .stat(this.pathForKey(key))
            .then((stat) => {
                this.log(`stat for ${key}`, stat);
                return stat || null;
            })
            .catch(() => null);
    }

    // noinspection JSUnusedGlobalSymbols
    getList(key, def = []) {
        return this.get(key)
            .then((r) => {
                this.log(`getList for ${key}`, r);
                return r || def;
            });
    }

    getObject(key, def = {}) {
        return this.get(key)
            .then((r) => {
                this.log(`getObject for ${key}`, r);
                return r || def;
            })
            .catch((ex) => {
                console.log('!!! LocalStorage getObject ex', ex);
                return def;
            });
    }

    async has(key) {
        if (key in this.cache) {
            return true;
        }
        const list = await this.fs.ls(`${this.fs.dirs.DocumentDir}/storage`);
        const result = !!list.some(key);
        this.log(`has ${key}`, result);
        return result;
    }

    async get(key) {
        if (key in this.cache) {
            // console.log('get cached', key, this.cache[key]);
            this.log(`get cached for ${key}`, this.cache[key]);
            return this.cache[key];
        }
        try {
            const path = this.pathForKey(key);
            const exists = await this.fs.exists(path);
            if (!exists) {
                this.log(`get for ${key} - file does not exist`);
                return null;
            }
            const val = await this.fs.readFile(path, ENCODING);
            // console.log('get', key, val);
            if (val) {
                const unpacked = val.slice(0, 3) === '$$$' ? await unpack(val.slice(3)) : val;
                this.cache[key] = JSON.parse(unpacked);
            }
            this.log(`get for ${key}`, this.cache[key]);
            return this.cache[key];
        } catch (ex) {
            this.log(`get for ${key} - ex`, ex);
        }
        this.log(`get for ${key} - failed to read`, null);
        return null;
    }

    async getForProfile(key, defaultValue = null) {
        const data = await this.get(key);
        if (!data || typeof data !== 'object') {
            this.log(`getForProfile for ${key} - no data or not an object`, data);
            return defaultValue;
        }
        const result = (data && this.profileId in data) ? data[this.profileId] : defaultValue;
        this.log(`getForProfile for ${key}`, { result, data, profile: this.profileId });
        return result;
    };

    async setForProfile(key, val) {
        let data = await this.get(key);
        if (data == null || typeof data !== 'object') {
            data = {};
        }
        data[this.profileId] = val;
        const result = this.set(key, data);
        this.log(`setForProfile for ${key}`, { result, data, profile: this.profileId });
        return result;
    }

    async set(key, val) {
        this.cache[key] = val;
        try {
            const str = JSON.stringify(val);
            // console.log('set to storage', key, str);
            const packed = str; // (MODE === 'dev' || (key === 'ums' && Platform.OS !== 'web')) ? str : `$$$${await pack(str)}`;
            await this.fs.writeFile(this.pathForKey(key), packed, ENCODING);
            this.log(`set for ${key}`, { val, str, packed });
            return val;
        } catch (ex) {
            this.log(`set for ${key} - ex`, { ex, val });
        }
        this.log(`set for ${key} - failed to write`, null);
        return null;
    }

    async assign(key, val) {
        const prevVal = await this.get(key, val);
        // noinspection UnnecessaryLocalVariableJS
        const nextVal = await this.set(
            key,
            prevVal
                ? {
                    ...prevVal,
                    ...val,
                }
                : { ...val },
        );
        return nextVal;
    }

    remove(...keys) {
        const { cache } = this;
        keys.forEach((k) => {
            this.log(`remove ${k} from cache`);
            delete cache[k];
        });
        const filterFn = keys ? (p) => keys.includes(p) : () => true;
        return new Promise((resolve, reject) =>
            this.fs
                .ls(`${this.fs.dirs.DocumentDir}/storage`)
                .then((files) =>
                    Promise.all(files.filter(filterFn)
                        .map((k) => this.fs.writeFile(this.pathForKey(k), '', ENCODING))),
                )
                .then(() => this.fs.ls(`${this.fs.dirs.DocumentDir}/storage`))
                // eslint-disable-next-line no-console
                .then((files) => this.log('removed', files))
                .then(resolve, reject),
        );
    }

    async getAll() {
        const list = await this.fs.ls(`${this.fs.dirs.DocumentDir}/storage`);
        const all = await Promise.all(list.map((k) => this.get(k)));
        const result = all.reduce((acc, item, i) => {
            acc[list[i]] = item;
            return acc;
        }, {});
        this.log('getAll list', { list, all, result });
        return result;
    }
}

export const storage = new LocalStorage(false);

export const useStoredValue = (key, def) => {
    const [ value, setValue ] = useState(def);
    useEffect(() => {
        (async () => {
            const d = await storage.get(key);
            if (d != null) {
                setValue(d);
            }
        })();
    }, []);
    return value;
};
